import React from 'react';
import { TrendingUp, TrendingDown, ChevronUp, ChevronDown, MoveUp, MoveDown, Shield, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/button';
import PlatformHistoryChart from './PlatformHistoryChart';

// Sort icon helper - renders the appropriate sort icon based on column state
const renderSortIcon = (sortColumn, sortDirection, column) => {
  if (sortColumn !== column) {
    return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
  }
  return sortDirection === 'asc' 
    ? <ArrowUp className="w-3 h-3 text-blue-600" />
    : <ArrowDown className="w-3 h-3 text-blue-600" />;
};

// Panel move buttons - standalone component
const PanelMoveButtons = ({ panelId, movePanelUp, movePanelDown, isFirst, isLast, lightTheme = false }) => (
  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelUp(panelId)}
      disabled={isFirst}
      className={`p-1 h-auto ${isFirst ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel up"
    >
      <MoveUp className="w-4 h-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      onClick={() => movePanelDown(panelId)}
      disabled={isLast}
      className={`p-1 h-auto ${isLast ? 'opacity-30 cursor-not-allowed' : lightTheme ? 'text-white/60 hover:text-white hover:bg-white/20' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'}`}
      title="Move panel down"
    >
      <MoveDown className="w-4 h-4" />
    </Button>
  </div>
);

const PlatformPerformancePanel = ({
  platforms,
  performance,
  showPlatformPerformance,
  togglePlatformPerformance,
  selectedPlatform,
  setSelectedPlatform,
  setShowStockList,
  mobilePreview,
  formatCurrencyPrivate,
  movePanelUp,
  movePanelDown,
  isFirst,
  isLast,
  platformSortColumn,
  platformSortDirection,
  handlePlatformSort,
  getSortedPlatforms,
  userEmail,
}) => {
  return (
    <div className={`${mobilePreview ? 'mb-2' : 'mb-2 sm:mb-4'} scroll-mt-32 sm:scroll-mt-0`}>
      <div className="bg-white rounded-xl shadow-md border-2 border-slate-200 overflow-hidden">
        {/* Mobile Header - Clickable to expand/collapse */}
        <div 
          className="sm:hidden bg-gradient-to-r from-cyan-600 to-blue-600 px-3 py-3 cursor-pointer"
          onClick={togglePlatformPerformance}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Platform Performance</h2>
                <p className="text-[10px] text-white/70">{platforms.length} platforms</p>
              </div>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg text-white">
              {showPlatformPerformance ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>
        
        {/* Desktop Header */}
        <div 
          className="hidden sm:block bg-gradient-to-r from-cyan-600 to-blue-600 px-4 sm:px-5 py-3 cursor-pointer"
          onClick={togglePlatformPerformance}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PanelMoveButtons panelId="platform" movePanelUp={movePanelUp} movePanelDown={movePanelDown} isFirst={isFirst} isLast={isLast} lightTheme={true} />
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Platform Performance</h2>
                <p className="text-xs text-white/70">{platforms.length} platforms in your portfolio</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); togglePlatformPerformance(); }}
              className="text-white/80 hover:text-white hover:bg-white/20 p-1 h-auto"
            >
              {showPlatformPerformance ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Platform Rows */}
        {showPlatformPerformance && platforms.length > 0 && (
          <>
            {/* Column Headers - Desktop Only */}
            <div className={mobilePreview ? 'hidden' : 'hidden sm:flex items-center justify-between px-4 sm:px-6 py-3 bg-gradient-to-r from-slate-200 to-slate-100 border-b-2 border-slate-300 text-xs font-bold text-slate-700 uppercase tracking-wide'}>
              <div className="flex items-center gap-4 flex-1">
                <button 
                  className="flex items-center gap-1 w-[90px] hover:text-slate-900 transition-colors"
                  onClick={() => handlePlatformSort('name')}
                >
                  Platform {renderSortIcon(platformSortColumn, platformSortDirection, 'name')}
                </button>
                <button 
                  className="flex items-center gap-1 w-[45px] hover:text-slate-900 transition-colors"
                  onClick={() => handlePlatformSort('holdings')}
                >
                  Pos {renderSortIcon(platformSortColumn, platformSortDirection, 'holdings')}
                </button>
                <button 
                  className="flex items-center gap-1 w-[110px] justify-end hover:text-slate-900 transition-colors"
                  onClick={() => handlePlatformSort('totalValue')}
                >
                  Value {renderSortIcon(platformSortColumn, platformSortDirection, 'totalValue')}
                </button>
                <button 
                  className="flex items-center gap-1 min-w-[200px] hover:text-slate-900 transition-colors"
                  onClick={() => handlePlatformSort('gainLoss')}
                >
                  Gain/Loss {renderSortIcon(platformSortColumn, platformSortDirection, 'gainLoss')}
                </button>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5">
                <span className="w-[55px] text-center font-bold">1D</span>
                <span className="w-[55px] text-center font-bold">1W</span>
                <span className="w-[55px] text-center font-bold">1M</span>
                <span className="w-[55px] text-center font-bold">1Y</span>
              </div>
            </div>
            <div>
              {getSortedPlatforms(platforms).map((platform, index) => {
                const isPositive = parseFloat(platform.gainLoss) >= 0;
                const platformPerf = performance?.byPlatform?.[platform.name];
                
                return (
                  <div key={platform.name} className={index > 0 ? 'border-t border-slate-200' : ''}>
                    {/* Platform Row - Responsive: stacks on mobile, single line on desktop */}
                    <div className={mobilePreview ? 'py-2 px-3 bg-white hover:bg-slate-50 transition-colors' : 'py-2 sm:py-3 px-4 sm:px-6 bg-white hover:bg-slate-50 transition-colors'}>
                      {/* Mobile Layout - Stacked */}
                      <div className={mobilePreview ? 'block space-y-1' : 'block sm:hidden space-y-2'}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span 
                              className={`font-bold text-slate-900 ${mobilePreview ? 'text-xs' : 'text-sm'} cursor-pointer hover:text-blue-600 hover:underline ${selectedPlatform === platform.name ? 'text-blue-600 underline' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPlatform(selectedPlatform === platform.name ? null : platform.name);
                                setShowStockList(true);
                              }}
                            >
                              {platform.name}
                            </span>
                            <span className="text-slate-500 text-xs">{platform.holdings} pos</span>
                          </div>
                          <span className={`text-slate-900 font-bold ${mobilePreview ? 'text-xs' : 'text-sm'}`}>${formatCurrencyPrivate(platform.totalValue)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            {isPositive ? (
                              <TrendingUp className="w-3 h-3 text-emerald-600" />
                            ) : (
                              <TrendingDown className="w-3 h-3 text-red-600" />
                            )}
                            <span className={`font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'} ${mobilePreview ? 'text-xs' : 'text-sm'}`}>
                              {isPositive ? '+' : ''}${formatCurrencyPrivate(platform.gainLoss)} ({platform.gainLossPercent}%)
                            </span>
                          </div>
                          {/* ALL metric on same line for non-mobile */}
                          {platformPerf && !mobilePreview && (
                            <span className={`text-xs font-semibold ${parseFloat(platformPerf.allTime?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {parseFloat(platformPerf.allTime?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf.allTime?.changePercent || 0}%
                            </span>
                          )}
                        </div>
                        {/* Time-based performance metrics - compact horizontal */}
                        {platformPerf && mobilePreview && (
                          <div className="flex items-center justify-between text-[9px] pt-0.5">
                            <div className="flex items-center gap-2">
                              <span className={`${parseFloat(platformPerf['1D']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} ${platformPerf['1D']?.estimated ? 'opacity-30' : ''}`}>
                                1D: {parseFloat(platformPerf['1D']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1D']?.changePercent || 0}%
                              </span>
                              <span className={`${parseFloat(platformPerf['1W']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} ${platformPerf['1W']?.estimated ? 'opacity-30' : ''}`}>
                                1W: {parseFloat(platformPerf['1W']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1W']?.changePercent || 0}%
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`${parseFloat(platformPerf['1M']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} ${platformPerf['1M']?.estimated ? 'opacity-30' : ''}`}>
                                1M: {parseFloat(platformPerf['1M']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1M']?.changePercent || 0}%
                              </span>
                              <span className={`${parseFloat(platformPerf['1Y']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'} ${platformPerf['1Y']?.estimated ? 'opacity-30' : ''}`}>
                                1Y: {parseFloat(platformPerf['1Y']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1Y']?.changePercent || 0}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Desktop Layout - Single Line */}
                      <div className={mobilePreview ? 'hidden' : 'hidden sm:flex items-center justify-between'}>
                        <div className="flex items-center gap-4 flex-1">
                          <span 
                            className={`font-bold text-slate-900 text-sm w-[90px] cursor-pointer hover:text-blue-600 hover:underline ${selectedPlatform === platform.name ? 'text-blue-600 underline' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlatform(selectedPlatform === platform.name ? null : platform.name);
                              setShowStockList(true);
                            }}
                          >
                            {platform.name}
                          </span>
                          <span className="text-slate-600 text-xs w-[45px] bg-slate-100 px-2 py-0.5 rounded-full text-center">{platform.holdings} pos</span>
                          <span className="text-slate-900 font-bold text-sm w-[110px] text-right">${formatCurrencyPrivate(platform.totalValue)}</span>
                          {/* Gain/Loss column - same format as 1D, 1W columns */}
                          <div className="flex flex-col items-center w-[90px]">
                            <div className="flex items-center gap-1">
                              {isPositive ? (
                                <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                              ) : (
                                <TrendingDown className="w-3 h-3 text-red-600 flex-shrink-0" />
                              )}
                              <span className={`font-bold text-xs ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                {isPositive ? '+' : ''}${formatCurrencyPrivate(platform.gainLoss)}
                              </span>
                            </div>
                            <span className={`text-[10px] ${isPositive ? 'text-emerald-500' : 'text-red-500'}`}>
                              {isPositive ? '+' : ''}{platform.gainLossPercent}%
                            </span>
                          </div>
                        </div>
                        
                        {/* Desktop Performance metrics - show $ amounts without duplicate labels */}
                        {platformPerf && (
                          <div className="flex items-center gap-1">
                            {/* 1D */}
                            <div className="flex flex-col items-center justify-center w-[75px] bg-slate-50 px-2 py-1.5 rounded">
                              <span className={`font-bold text-xs ${parseFloat(platformPerf['1D']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {parseFloat(platformPerf['1D']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyPrivate(Math.abs(platformPerf['1D']?.change || 0).toFixed(0))}
                              </span>
                              <span className={`text-[10px] ${parseFloat(platformPerf['1D']?.change || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {parseFloat(platformPerf['1D']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1D']?.changePercent || 0}%
                              </span>
                            </div>
                            {/* 1W */}
                            <div className="flex flex-col items-center justify-center w-[75px] bg-slate-50 px-2 py-1.5 rounded">
                              <span className={`font-bold text-xs ${parseFloat(platformPerf['1W']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {parseFloat(platformPerf['1W']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyPrivate(Math.abs(platformPerf['1W']?.change || 0).toFixed(0))}
                              </span>
                              <span className={`text-[10px] ${parseFloat(platformPerf['1W']?.change || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {parseFloat(platformPerf['1W']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1W']?.changePercent || 0}%
                              </span>
                            </div>
                            {/* 1M */}
                            <div className="flex flex-col items-center justify-center w-[75px] bg-slate-50 px-2 py-1.5 rounded">
                              <span className={`font-bold text-xs ${parseFloat(platformPerf['1M']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {parseFloat(platformPerf['1M']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyPrivate(Math.abs(platformPerf['1M']?.change || 0).toFixed(0))}
                              </span>
                              <span className={`text-[10px] ${parseFloat(platformPerf['1M']?.change || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {parseFloat(platformPerf['1M']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1M']?.changePercent || 0}%
                              </span>
                            </div>
                            {/* 1Y */}
                            <div className="flex flex-col items-center justify-center w-[75px] bg-slate-50 px-2 py-1.5 rounded">
                              <span className={`font-bold text-xs ${parseFloat(platformPerf['1Y']?.change || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {parseFloat(platformPerf['1Y']?.change || 0) >= 0 ? '+' : ''}${formatCurrencyPrivate(Math.abs(platformPerf['1Y']?.change || 0).toFixed(0))}
                              </span>
                              <span className={`text-[10px] ${parseFloat(platformPerf['1Y']?.change || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {parseFloat(platformPerf['1Y']?.changePercent || 0) >= 0 ? '+' : ''}{platformPerf['1Y']?.changePercent || 0}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Historical Performance Chart */}
            <PlatformHistoryChart 
              userEmail={userEmail}
              formatCurrency={formatCurrencyPrivate}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PlatformPerformancePanel;
